import { ArrowLeft } from "lucide-react";
import type * as React from "react";

/**
 * AdminDetailSection — a shared, in-page drill-down takeover for the admin
 * shell (Jul 2026). A "place you navigate to", not a floating overlay.
 *
 * Rationale (SOTA / "Claude design"): deep, data-heavy drill-downs (a reasoning
 * trace's tool timeline, a cost trace's LLM-call waterfall) are destination
 * work. A drawer is too narrow and a modal-over-modal (the admin shell is
 * already a fullscreen modal) reads as transient. A dedicated inline section —
 * with a "← <parent>" back affordance and a URL-addressable ``?trace=`` param
 * wired by the route — is URL-addressable, back/forward works, no z-index
 * stacking.
 *
 * Normal document flow (no inner scroll): the admin content area scrolls inside
 * ``SurfaceLayout``, so the body flows naturally and the whole surface scrolls
 * as one — no magic height calc that could clip long waterfalls.
 *
 * Reused by ``HmReasoningTracesTab`` (reasoning-trace detail) and
 * ``AdminCostTracking`` (cost-trace waterfall). Pencil chrome: cream card,
 * hairline divider, one orange back-link accent.
 */
export interface AdminDetailSectionProps {
  /** Return to the list (clears the ``?trace`` search param). */
  readonly onBack: () => void;
  /** Label for the back affordance, e.g. "Reasoning traces". */
  readonly backLabel: React.ReactNode;
  /** Section title (the record being inspected). */
  readonly title: React.ReactNode;
  /** Optional monospace/secondary line under the title (e.g. the turn id). */
  readonly subtitle?: React.ReactNode;
  /** Optional right-aligned header controls. */
  readonly headerActions?: React.ReactNode;
  readonly children: React.ReactNode;
}

export const AdminDetailSection: React.FC<AdminDetailSectionProps> = ({
  onBack,
  backLabel,
  title,
  subtitle,
  headerActions,
  children,
}) => (
  <div className="space-y-3">
    <button
      type="button"
      onClick={onBack}
      className="inline-flex w-fit items-center gap-1 rounded-md py-1 pr-2 font-medium text-[12.5px] text-ink-3 transition-colors hover:text-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
    >
      <ArrowLeft size={15} strokeWidth={2} aria-hidden />
      {backLabel}
    </button>

    <div className="overflow-hidden rounded-xl border border-hairline bg-cream/40">
      <header className="flex items-start justify-between gap-3 border-hairline border-b px-5 py-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-ink-1 text-sm">{title}</h3>
          {subtitle ? (
            <p className="mt-0.5 truncate font-mono text-[11px] text-ink-3">{subtitle}</p>
          ) : null}
        </div>
        {headerActions ? (
          <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
        ) : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </div>
  </div>
);
AdminDetailSection.displayName = "AdminDetailSection";
